/**
 * useWorkOrderStatusChange.ts — P2-4 (scheduling audit #2)
 *
 * The WO status control's schedule seam. Originally lived beside the (since
 * deleted) orphaned pages/workorders/WorkOrderDetail.tsx; moved to hooks/ when
 * the schedule features were ported to the live WorkOrdersTabContent surface
 * (Phase 2, qa/quote-wo-schedule-lifecycle-2026-07-11):
 *   - 'Cancelled' releases the WO's bookings + staffing and auto-deletes draft
 *     plans. Committed work is guarded: the backend 409s with
 *     SOURCE_RELEASES_COMMITTED, we raise the confirm ceremony ("Release &
 *     cancel" on the destructive SECONDARY button — delete-door layout) and
 *     retry with releaseSchedule=true.
 *   - 'Completed' silently trims residual future days; the counts are
 *     reported in the snackbar.
 * Both refresh the schedule banner + commit status so the page reflects the
 * release without a reload.
 */

import { useCallback } from 'react';
import type { ConfirmDialogState } from '../components/shared/ConfirmDialog';
import { updateWorkOrder } from '../services/workOrderService';
import type { WorkOrder } from '../types/workOrder';

type Snackbar = { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' };

interface UseWorkOrderStatusChangeArgs {
  workOrderId: string | undefined;
  setWorkOrder: React.Dispatch<React.SetStateAction<WorkOrder | null>>;
  setSnackbar: (s: Snackbar) => void;
  setConfirmDlg: (s: ConfirmDialogState) => void;
  /** Refresh the ScheduleLinkBanner impact after a release/trim. */
  refetchImpact: () => Promise<unknown>;
  /** Refresh the Commit Schedule button state after a release/trim. */
  refreshCommitStatus: () => Promise<void>;
}

export function useWorkOrderStatusChange({
  workOrderId,
  setWorkOrder,
  setSnackbar,
  setConfirmDlg,
  refetchImpact,
  refreshCommitStatus,
}: UseWorkOrderStatusChangeArgs): (statusKey: string, label: string) => Promise<void> {
  const applyStatusChange = useCallback(async (
    statusKey: string,
    label: string,
    releaseSchedule = false,
  ) => {
    if (!workOrderId) return;
    const result = await updateWorkOrder(
      workOrderId,
      { WorkOrderStatus: statusKey as WorkOrder['WorkOrderStatus'] },
      releaseSchedule ? { releaseSchedule: true } : undefined,
    );
    setWorkOrder((prev) => prev ? { ...prev, WorkOrderStatus: statusKey } : prev);
    const rel = result.scheduleRelease;
    const trim = result.scheduleTrim;
    let message = `Status changed to ${label}`;
    if (rel) {
      message += ` — released ${rel.bookingsReleased} booking(s)`;
      if ((rel.assignmentsReleased ?? 0) > 0) message += ` and ${rel.assignmentsReleased} assignment(s)`;
      message += ' from the schedule';
      if (result.scenariosDeleted) message += `; ${result.scenariosDeleted} draft plan(s) deleted`;
      message += '.';
    } else if (trim) {
      const parts: string[] = [];
      if (trim.bookingsReleased) parts.push(`released ${trim.bookingsReleased} future booking(s)`);
      if (trim.bookingsTrimmed) parts.push(`trimmed ${trim.bookingsTrimmed} booking(s) to today`);
      if (trim.assignmentsReleased) parts.push(`released ${trim.assignmentsReleased} staffing row(s)`);
      message += ` — ${parts.join(', ')}.`;
    }
    setSnackbar({ open: true, message, severity: 'success' });
    if (rel || trim) {
      await refetchImpact();
      await refreshCommitStatus();
    }
  }, [workOrderId, setWorkOrder, setSnackbar, refetchImpact, refreshCommitStatus]);

  return useCallback(async (statusKey: string, label: string) => {
    try {
      await applyStatusChange(statusKey, label);
    } catch (err) {
      const resp = (err as {
        response?: {
          status?: number;
          data?: { code?: string; error?: string; committedBookings?: number; taskNames?: string[] };
        };
      }).response;
      if (resp?.status === 409 && resp.data?.code === 'SOURCE_RELEASES_COMMITTED') {
        const committed = resp.data.committedBookings ?? 0;
        const tasks = resp.data.taskNames ?? [];
        setConfirmDlg({
          open: true,
          title: 'Cancel work order — live schedule attached',
          message:
            `Cancelling this work order will release ${committed} committed booking(s) from the live schedule` +
            (tasks.length > 0 ? `:\n\n${tasks.map((n) => `• ${n}`).join('\n')}\n\n` : '. ') +
            'This cannot be undone. Choose "Release & cancel" to continue.',
          confirmLabel: 'Keep schedule',
          confirmColor: 'primary',
          // Confirm button is the SAFE path (closes the dialog); the
          // destructive release lives on the explicit secondary button —
          // same ceremony as the delete door.
          onConfirm: () => { /* safe no-op */ },
          secondaryLabel: 'Release & cancel work order',
          secondaryColor: 'error',
          onSecondary: () => {
            void applyStatusChange(statusKey, label, true).catch(() => {
              setSnackbar({ open: true, message: 'Failed to change status', severity: 'error' });
            });
          },
        });
        return;
      }
      setSnackbar({
        open: true,
        message: resp?.data?.error ?? 'Failed to change status',
        severity: 'error',
      });
    }
  }, [applyStatusChange, setConfirmDlg, setSnackbar]);
}
